import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare, faTimes, faHistory } from "@fortawesome/free-solid-svg-icons";
import { format, parseISO, isValid, formatDistanceToNow } from "date-fns";
import { IPriceQuoteRequest } from "../../../services/booking-service";
import "./PreviousSearches.scss";

export interface ISavedSearch {
  id: string;
  tabId?: string;
  searchData: IPriceQuoteRequest;
  departureStationCode: string;
  returnStationCode: string;
  departureStationName?: string;
  returnStationName?: string;
  departureDate: string; // ISO yyyy-MM-dd
  returnDate: string; // ISO yyyy-MM-dd
  durationDays: number;
  timestamp: number;
}

interface IPreviousSearchesProps {
  searches: ISavedSearch[];
  onSelectSearch: (search: ISavedSearch) => void;
  onOpenInNewTab: (search: ISavedSearch) => void;
  onDeleteSearch: (id: string) => void;
  onClearAll: () => void;
}

const formatDateRange = (depStr: string, retStr: string, days: number): string => {
  try {
    const dep = parseISO(depStr);
    const ret = parseISO(retStr);
    if (!isValid(dep) || !isValid(ret)) {
      return `${depStr} - ${retStr}`;
    }
    const depFormatted = format(dep, "dd/MM");
    const retFormatted = format(ret, "dd/MM");
    const dayLabel = days === 1 ? "day" : "days";
    return `${depFormatted} - ${retFormatted} (${days} ${dayLabel})`;
  } catch (e) {
    return `${depStr} - ${retStr}`;
  }
};

const formatRelativeTime = (timestamp: number): string => {
  try {
    const diffSec = Math.floor((Date.now() - timestamp) / 1000);
    if (diffSec < 45) {
      return "Just now";
    }
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  } catch (e) {
    return "";
  }
};

export const PreviousSearches: React.FC<IPreviousSearchesProps> = ({
  searches,
  onSelectSearch,
  onOpenInNewTab,
  onDeleteSearch,
  onClearAll,
}) => {
  // Trigger periodic re-render every 30 seconds to update relative timestamps
  const [, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setTick(t => t + 1);
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  const handleClearHistory = () => {
    if (window.confirm("Clear all previous search history?")) {
      onClearAll();
    }
  };

  return (
    <div className="previous-searches-panel">
      <div className="panel-header">
        <h3 className="header-title">
          <FontAwesomeIcon icon={faHistory} className="icon" />
          Previous Searches
          {searches.length > 0 && (
            <span className="count-badge">{searches.length}</span>
          )}
        </h3>
      </div>

      {searches.length === 0 ? (
        <div className="empty-state">
          No previous searches yet. Your searches will be automatically saved here.
        </div>
      ) : (
        <>
          <div className="searches-list">
            {searches.map((search) => {
              const fullRouteTitle = search.departureStationName && search.returnStationName
                ? `${search.departureStationName} → ${search.returnStationName}`
                : `${search.departureStationCode} → ${search.returnStationCode}`;

              return (
                <div
                  key={search.id}
                  className="search-card"
                  onClick={() => onSelectSearch(search)}
                  title={`Load search: ${fullRouteTitle}`}
                >
                  <div className="card-top-row">
                    <div className="stations" title={fullRouteTitle}>
                      <span>{search.departureStationCode}</span>
                      <span className="arrow">→</span>
                      <span>{search.returnStationCode}</span>
                    </div>
                    <div className="card-actions">
                      <button
                        type="button"
                        className="action-btn new-tab-btn"
                        title="Open in new search tab"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenInNewTab(search);
                        }}
                      >
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                      </button>
                      <button
                        type="button"
                        className="action-btn delete-btn"
                        title="Remove from history"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteSearch(search.id);
                        }}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  </div>

                  <div className="card-date-row">
                    <span className="date-text">
                      {formatDateRange(search.departureDate, search.returnDate, search.durationDays)}
                    </span>
                    <span className="time-ago">
                      {formatRelativeTime(search.timestamp)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="panel-footer">
            <button
              type="button"
              className="clear-history-btn"
              onClick={handleClearHistory}
            >
              Clear all history
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PreviousSearches;
